// Versioned, attribute-free HTML subset inside existing text columns. Legacy
// strings are always text, never interpreted as HTML. No historical rewrites.
export const RICH_PREFIX = "places-weekly-rich:v1:";
const TAG = /^(\/?)(b|i|ul|ol|li|br)$/;
const escape = text => text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

export function richBody(value) {
  if (typeof value !== "string" || !value.startsWith(RICH_PREFIX)) return null;
  const html = value.slice(RICH_PREFIX.length);
  const stack = [];
  let offset = 0;
  for (const match of html.matchAll(/<([^>]*)>/g)) {
    if (html.slice(offset, match.index).includes("<")) throw new Error("Invalid weekly formatting");
    const tag = TAG.exec(match[1]);
    if (!tag) throw new Error("Only bold, italic and lists are supported");
    const [, closing, name] = tag;
    if (closing) {
      if (name === "br" || stack.pop() !== name) throw new Error("Invalid weekly formatting");
    } else if (name !== "br") {
      if (stack.length >= 24) throw new Error("Weekly formatting is too deeply nested");
      if (name === "li" && !["ul", "ol"].includes(stack.at(-1))) throw new Error("Invalid weekly list");
      stack.push(name);
    }
    offset = match.index + match[0].length;
  }
  if (stack.length || html.slice(offset).includes("<")) throw new Error("Invalid weekly formatting");
  return html;
}

export function validateWeeklyText(value, limit = 50000, required = false) {
  if (typeof value !== "string" || value.length > limit) throw new Error(`Weekly text must be at most ${limit} characters`);
  const html = richBody(value);
  if (html === null && /<\/?[a-z!][^>]*>/i.test(value)) throw new Error("Use the weekly editor for supported formatting");
  if (required && !weeklyText(value).trim()) throw new Error("Weekly text is required");
  return value;
}

export function weeklyText(value) {
  if (Array.isArray(value)) return value.map(weeklyText).join("\n");
  const text = typeof value === "string" ? value : "";
  let html;
  try { html = richBody(text); } catch { return "Unsupported weekly formatting"; }
  if (html === null) return text;
  return html.replace(/<br>|<\/li>|<\/?ul>|<\/?ol>/g, "\n").replace(/<[^>]*>/g, "")
    .replace(/&(?:amp|lt|gt|quot|#39|nbsp);/g, entity => ({ "&amp;": "&", "&lt;": "<", "&gt;": ">", "&quot;": '"', "&#39;": "'", "&nbsp;": " " })[entity]).replace(/\n{2,}/g, "\n").trim();
}

export function weeklyHtml(value) {
  try {
    const html = richBody(value);
    if (html !== null) return html;
  } catch { return escape("Unsupported weekly formatting"); }
  return escape(weeklyText(value)).replace(/\n/g, "<br>");
}

// The browser parser is used only at the editing boundary. Rebuild from known
// elements, discarding attributes and active-content subtrees entirely.
export function serializeWeeklyDom(root) {
  const walk = node => {
    if (node.nodeType === 3) return escape(node.textContent);
    if (node.nodeType !== 1) return "";
    const name = node.tagName.toLowerCase();
    if (["script", "style", "iframe", "object", "svg", "math", "template"].includes(name)) return "";
    const body = [...node.childNodes].map(walk).join("");
    const tag = ({ strong: "b", em: "i" })[name] || name;
    if (tag === "li" && !["UL", "OL"].includes(node.parentElement?.tagName)) return `<ul><li>${body}</li></ul>`;
    if (["b", "i", "ul", "ol", "li"].includes(tag)) return `<${tag}>${body}</${tag}>`;
    if (tag === "br") return "<br>";
    return body + (["div", "p"].includes(tag) ? "<br>" : "");
  };
  const html = [...root.childNodes].map(walk).join("");
  if (!html.includes("<")) return weeklyText(RICH_PREFIX + html);
  const value = RICH_PREFIX + html;
  richBody(value);
  return weeklyText(value).trim() ? value : "";
}
